import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUserSafe } from "@/contexts/UserContext";
import {
  Store,
  Map,
  Grid,
  HelpCircle,
  X,
  User,
  ShoppingBag,
  MessageCircle,
  Heart,
  LogOut,
  Hotel,
} from "lucide-react";

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}

/**
 * MobileMenu - Mobile slide-out menu
 * 
 * Now uses UserContext instead of calling getUser() directly.
 */
export default function MobileMenu({ open, onClose }: MobileMenuProps) {
  const { t, i18n } = useTranslation('common');
  const userContext = useCurrentUserSafe();
  const user = userContext?.user;
  const isSeller = userContext?.isSeller ?? false;

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    i18n.changeLanguage(e.target.value);
    document.documentElement.dir = e.target.value === "he" ? "rtl" : "ltr";
  };

  return (
    <div className="lc-menu-overlay" onClick={onClose}>
      <aside
        className="lc-menu"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="lc-menu-header flex items-center justify-between">
          <span className="text-lg font-semibold text-foreground">Menu</span>
          <button
            onClick={onClose}
            className="rounded-full p-2 hover:bg-muted transition-colors"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="lc-menu-scroll space-y-4">
          {/* Auth Section - Compact */}
          {!user ? (
            <Link to="/auth" onClick={onClose}>
              <button className="w-full rounded-xl bg-primary py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors">
                Sign In
              </button>
            </Link>
          ) : (
            <div className="space-y-2">
              <Link to={isSeller ? "/dashboard" : "/account"} onClick={onClose}>
                <button className="w-full rounded-xl bg-primary py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors">
                  My Account
                </button>
              </Link>
            </div>
          )}

          <div className="h-px bg-border/60" />

          {/* User Menu - Only when logged in */}
          {user && (
            <>
              <nav className="space-y-1 text-sm">
                <Link
                  to="/orders"
                  onClick={onClose}
                  className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <ShoppingBag className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium">My Orders</span>
                </Link>

                <Link
                  to="/buyer-chats"
                  onClick={onClose}
                  className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <MessageCircle className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium">My Chats</span>
                </Link>

                <Link
                  to="/wishlist"
                  onClick={onClose}
                  className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <Heart className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium">My Favorites</span>
                </Link>

                <Link
                  to="/account"
                  onClick={onClose}
                  className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <User className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium">My Account</span>
                </Link>
              </nav>

              <div className="h-px bg-border/60" />
            </>
          )}

          {/* Main Navigation */}
          <nav className="space-y-1 text-sm">
            {!isSeller && (
              <Link
                to="/seller-onboarding"
                onClick={onClose}
                className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <Store className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">Sell on LocalCraft</span>
              </Link>
            )}

            <Link
              to="/stays"
              onClick={onClose}
              className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <Hotel className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">{t('common:nav.stays', 'Stays')}</span>
            </Link>

            <Link
              to="/bundles"
              onClick={onClose}
              className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <Grid className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">{t('common:nav.bundles', 'Bundles')}</span>
            </Link>

            <Link
              to="/explore"
              onClick={onClose}
              className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <Map className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">Explore by Region</span>
            </Link>

            <Link
              to="/categories"
              onClick={onClose}
              className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <Grid className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">Categories</span>
            </Link>

            <Link
              to="/support"
              onClick={onClose}
              className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <HelpCircle className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">Help / Support</span>
            </Link>
          </nav>

          <div className="h-px bg-border/60" />

          {/* Language Selector - Compact */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1">
              Language
            </label>
            <select
              value={i18n.language}
              onChange={handleLanguageChange}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow"
            >
              <option value="en">English</option>
              <option value="he">עברית (Hebrew)</option>
              <option value="es">Español</option>
              <option value="ru">Русский</option>
            </select>
          </div>

          {/* Logout Button - Only when logged in */}
          {user && (
            <>
              <div className="h-px bg-border/60" />
              <button
                onClick={async () => {
                  await supabase.auth.signOut();
                  onClose();
                  window.location.href = "/";
                }}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border border-border hover:bg-muted/50 transition-colors text-sm font-medium"
              >
                <LogOut className="h-4 w-4" />
                <span>Sign Out</span>
              </button>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
